import React, { useRef, useEffect } from 'react';
import Link from 'next/link';
import cx from 'classnames';
import type { Hit as AlgoliaHitType } from 'instantsearch.js';

import styles from 'src/components/docs/Search/SearchHit.module.scss';
import { decodeHtmlEntities } from 'src/lib/decode-html-entities';

// Parse Algolia highlight tags and render as React elements (safer than dangerouslySetInnerHTML)
const parseHighlightedText = (text: string): React.ReactNode[] => {
  // Algolia uses <mark> tags for highlights (can also be <em> depending on config)
  const parts = text.split(/(<mark>.*?<\/mark>|<em>.*?<\/em>)/g);
  return parts.map((part, index) => {
    const markMatch = part.match(/^<mark>(.*?)<\/mark>$/);
    const emMatch = part.match(/^<em>(.*?)<\/em>$/);
    if (markMatch) {
      return <mark key={index}>{decodeHtmlEntities(markMatch[1])}</mark>;
    }
    if (emMatch) {
      return <mark key={index}>{decodeHtmlEntities(emMatch[1])}</mark>;
    }
    return decodeHtmlEntities(part);
  });
};

// Render highlighted text from Algolia's _highlightResult
const HighlightedText: React.FC<{
  hit: AlgoliaHitType<HitFields>;
  attribute: string;
  className?: string;
}> = ({ hit, attribute, className }) => {
  const getNestedValue = (obj: unknown, path: string): string | undefined => {
    const parts = path.split('.');
    let current: unknown = obj;
    for (const part of parts) {
      if (current == null || typeof current !== 'object') return undefined;
      current = (current as Record<string, unknown>)[part];
    }
    if (current && typeof current === 'object' && 'value' in current) {
      return (current as { value: string }).value;
    }
    return undefined;
  };

  const highlightedValue = getNestedValue(hit._highlightResult, attribute);

  if (!highlightedValue) {
    // Fall back to raw value
    const rawValue = getNestedValue(hit, attribute);
    return <span className={className}>{rawValue || ''}</span>;
  }

  return (
    <span className={className}>{parseHighlightedText(highlightedValue)}</span>
  );
};

interface HitHierarchy {
  lvl0?: string;
  lvl1?: string;
  lvl2?: string;
  lvl3?: string;
  lvl4?: string;
  lvl5?: string;
}

interface HitFields {
  objectID: string;
  url: string;
  url_without_anchor?: string;
  anchor?: string;
  content?: string;
  type?: string;
  hierarchy?: HitHierarchy;
  __position?: number;
}

type Hit = AlgoliaHitType<HitFields>;

interface SearchHitProps {
  hit: Hit;
  isSelected: boolean;
  setURL: (url: string) => void;
  sendEvent: (eventType: string, hit: Hit, eventName: string) => void;
  onNavigate: (url: string) => void;
}

// Convert full URL to relative path for Next.js routing
const getRelativePath = (url: string): string => {
  try {
    const urlObj = new URL(url);
    return urlObj.pathname + urlObj.hash;
  } catch {
    return url;
  }
};

// Extract anchor from URL hash and format it for display
const getAnchorFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hash) {
      // Remove the # and convert kebab-case to readable text
      return urlObj.hash
        .slice(1)
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => {
          return c.toUpperCase();
        });
    }
  } catch {
    // Try to extract from relative URL
    const hashIndex = url.indexOf('#');
    if (hashIndex !== -1) {
      return url
        .slice(hashIndex + 1)
        .replace(/-/g, ' ')
        .replace(/\b\w/g, (c) => {
          return c.toUpperCase();
        });
    }
  }
  return null;
};

// Normalize text for comparison (lowercase, remove punctuation/spaces)
const normalizeForComparison = (text: string): string => {
  return text.toLowerCase().replace(/[^a-z0-9]/g, '');
};

// Check if anchor is redundant (similar to any hierarchy level)
const isAnchorRedundant = (
  anchor: string | null,
  hierarchy?: HitHierarchy,
): boolean => {
  if (!anchor || !hierarchy) return false;
  const normalizedAnchor = normalizeForComparison(anchor);
  const levels = [
    hierarchy.lvl0,
    hierarchy.lvl1,
    hierarchy.lvl2,
    hierarchy.lvl3,
    hierarchy.lvl4,
    hierarchy.lvl5,
  ].filter(Boolean) as string[];
  return levels.some((level) => {
    return normalizeForComparison(level) === normalizedAnchor;
  });
};

// Get all hierarchy levels that exist (for showing full path)
const getHierarchyLevels = (
  hierarchy?: HitHierarchy,
): { level: string; attribute: string }[] => {
  if (!hierarchy) return [];
  const levels: { level: string; attribute: string }[] = [];
  if (hierarchy.lvl2)
    levels.push({ level: 'lvl2', attribute: 'hierarchy.lvl2' });
  if (hierarchy.lvl3)
    levels.push({ level: 'lvl3', attribute: 'hierarchy.lvl3' });
  if (hierarchy.lvl4)
    levels.push({ level: 'lvl4', attribute: 'hierarchy.lvl4' });
  if (hierarchy.lvl5)
    levels.push({ level: 'lvl5', attribute: 'hierarchy.lvl5' });
  return levels;
};

// Check if hierarchy level exists
const hasHierarchyLevel = (
  hierarchy: HitHierarchy | undefined,
  level: string,
): boolean => {
  if (!hierarchy) return false;
  return Boolean(hierarchy[level as keyof HitHierarchy]);
};

const SearchHit: React.FC<SearchHitProps> = ({
  setURL,
  isSelected,
  hit,
  sendEvent,
  onNavigate,
}) => {
  const linkRef = useRef<HTMLAnchorElement>(null);

  const relativePath = getRelativePath(hit.url);
  const hierarchyLevels = getHierarchyLevels(hit.hierarchy);
  const hasLvl0 = hasHierarchyLevel(hit.hierarchy, 'lvl0');
  const hasLvl1 = hasHierarchyLevel(hit.hierarchy, 'lvl1');
  const rawAnchor = getAnchorFromUrl(hit.url);
  // Only show anchor if it provides additional context beyond the hierarchy
  const anchor = isAnchorRedundant(rawAnchor, hit.hierarchy) ? null : rawAnchor;

  useEffect(() => {
    if (linkRef.current != null && isSelected) {
      linkRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
      setURL(relativePath);
    }
  }, [isSelected, setURL, relativePath]);

  if (!hit.url) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    sendEvent('click', hit, 'Hit Clicked');
    onNavigate(relativePath);
  };

  // Always show content preview - use actual content or fall back to section heading
  const hasContent = Boolean(hit.content);

  return (
    <Link
      href={relativePath}
      key={hit.objectID}
      tabIndex={hit.__position}
      className={cx(isSelected && styles.selected, styles.hit)}
      onClick={handleClick}
      ref={linkRef}
    >
      <span className={styles.container}>
        <div className={styles.categoryHeader}>
          {hasLvl0 && (
            <HighlightedText
              className={styles.noShrink}
              hit={hit}
              attribute='hierarchy.lvl0'
            />
          )}
          {hasLvl0 && hasLvl1 && (
            <span className={styles.separator}>{' > '}</span>
          )}
          {hasLvl1 && (
            <HighlightedText
              className={styles.noShrink}
              hit={hit}
              attribute='hierarchy.lvl1'
            />
          )}
          {hierarchyLevels.map((lvl, index) => {
            return (
              <React.Fragment key={lvl.level}>
                <span className={styles.separator}>{' > '}</span>
                <HighlightedText
                  className={
                    index === hierarchyLevels.length - 1 && !anchor
                      ? styles.headerHits
                      : styles.noShrink
                  }
                  hit={hit}
                  attribute={lvl.attribute}
                />
              </React.Fragment>
            );
          })}
          {anchor && (
            <>
              <span className={styles.separator}>{' > '}</span>
              <span className={styles.headerHits}>{anchor}</span>
            </>
          )}
        </div>
        {hasContent ? (
          <HighlightedText
            className={styles.hitDescription}
            hit={hit}
            attribute='content'
          />
        ) : (
          <span className={styles.hitDescription}>
            {hierarchyLevels.length > 0
              ? hit.hierarchy?.[
                  hierarchyLevels[hierarchyLevels.length - 1]
                    .level as keyof HitHierarchy
                ]
              : hit.hierarchy?.lvl1 || hit.hierarchy?.lvl0}
          </span>
        )}
      </span>
    </Link>
  );
};

SearchHit.displayName = 'CoreExchangeSearchHit';

export default SearchHit;
